import React from 'react';
import styled from 'styled-components';

// Forward reference
const DetailCard = styled.div`
  position: absolute;
  top: ${props => props.isSearchResult ? '50%' : '120px'};
  left: ${props => props.isSearchResult ? 'calc(80% + 1rem)' : '50%'};
  transform: ${props => props.isSearchResult ? 'translateY(-20%)' : 'translateX(-50%)'};
  background-color: #FFF8EB;
  border: 1px solid #2C3E50;
  padding: 1.5rem;
  padding-bottom: 0.5rem;
  border-radius: 4px;
  min-width: 240px;
  z-index: 1000;
  opacity: ${props => props.isSearchResult ? '1' : '0'};
  visibility: ${props => props.isSearchResult ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;

  @media (max-width: 768px) {
    top: ${props => props.isSearchResult ? 'calc(100% + 1rem)' : '120px'};
    left: ${props => props.isSearchResult ? '50%' : '50%'};
    transform: ${props => props.isSearchResult ? 'translateX(-50%)' : 'translateX(-50%)'};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const FlowerContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0rem;
  cursor: pointer;

  &:hover ${DetailCard} {
    opacity: 1;
    visibility: visible;
  }
`;

// Update DetailCard with the hover interaction
DetailCard.defaultProps = {
  isSearchResult: false
};

const FlowerSvg = styled.div`
  width: 150px;
  height: 150px;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const StockInfo = styled.div`
  text-align: center;
  margin-top: 0.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #2C3E50;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const DetailTitle = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #2C3E50;
  line-height: 1.4;
  text-transform: uppercase;
  font-weight: 500;
`;

const MetricLabel = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #2C3E50;
  font-weight: 500;
  margin-bottom:0.4rem;
`;

const MetricValue = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #2C3E50;
  margin-left: 0.25rem;
  font-weight: 500;
`;

const DetailText = styled.p`
  margin: 2rem 0;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  color: #2C3E50;
  text-align: left;

  &:first-of-type {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    line-height: 1.4;
    text-transform: none;
  }
`;

const Annotation = styled.span`
  display: block;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-style: regular;
  color: #2C3E50;
  margin-top: 0.5em;
  opacity: 0.7;
`;

const RecommendationTag = styled.div`
  padding: 0.25rem 0.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #2C3E50;
  border-radius: 2px;
  display: inline-block;
  border: 1px solid #2C3E50;
  background-color: ${props => {
    switch (props.type) {
      case 'buy':
        return '#DCE9A0';
      case 'sell':
        return '#FF7577';
      default:
        return '#D7CDFE';
    }
  }};
`;

const Legend = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px;
  border-top: 1px solid #eee;
  font-size: 14px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;

  .legend-content {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-bottom: 4px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }

  .disclaimer {
    font-size: 0.5rem;
    color: #2C3E50;
    margin-top: 4px;
    font-weight: 400;
  }
`;

const FlowerLegend = () => (
  <Legend>
    <div className="legend-content">
      <div className="legend-item">
        <div className="circle" style={{ background: '#98D8D8' }} />
        Buy
      </div>
      <div className="legend-item">
        <div className="circle" style={{ background: '#FA8072' }} />
        Sell
      </div>
      <div className="legend-item">
        <div className="circle" style={{ background: '#808080' }} />
        Hold
      </div>
      <div className="legend-item">
        Center size: Market Cap (2-6px, larger = bigger company)
      </div>
    </div>
    <div className="disclaimer">
      FYI these are just flowers not legal advice, the data is 15 minute delayed and the commentary like volatility, buy/sell/hold advice is AI generated
    </div>
  </Legend>
);

const Flower = ({ stock, isSearchResult }) => {
  const getRecommendation = () => {
    const change = parseFloat(stock.changePercent);
    const marketCap = parseFloat(stock.marketCapB);
    const volatility = parseFloat(stock.volatility);
    
    // Market cap categories
    const isLargeCap = marketCap >= 200;    // $200B+
    const isMidCap = marketCap >= 10;       // $10B - $200B
    const isSmallCap = marketCap < 10;      // < $10B

    // More conservative with small caps (need bigger gains to trigger buy)
    // More lenient with large caps (smaller gains can trigger buy due to stability)
    if (isLargeCap) {
      if (change > 0.5) return 'buy';      // 0.5% gain for large caps
      if (change < -1) return 'sell';      // -1% loss for large caps
    } else if (isMidCap) {
      if (change > 1) return 'buy';        // 1% gain for mid caps
      if (change < -1.5) return 'sell';    // -1.5% loss for mid caps
    } else {  // small caps
      if (change > 2 && volatility < 0.1) return 'buy';   // 2% gain and low volatility for small caps
      if (change < -2) return 'sell';      // -2% loss for small caps
    }
    
    return 'hold';
  };

  // Separate color logic from recommendation
  const getFlowerColor = () => {
    const change = parseFloat(stock.changePercent);
    if (change > 0) {
      return 'rgba(119, 213, 206, 0.7)'; // #77D5CE with 70% opacity - Teal for up
    } else if (change < 0) {
      return 'rgba(223, 154, 152, 0.7)'; // #DF9A98 with 70% opacity - Salmon for down
    } else {
      return 'rgba(197, 169, 166, 0.7)'; // #C5A9A6 with 70% opacity - Gray for no change
    }
  };

  // Calculate number of petals based on volume
  const getNumberOfPetals = () => {
    const volume = parseInt(stock.volume);
    
    // Direct mapping to petals (4-10 range)
    if (volume <= 1000000) return 6;
    if (volume <= 2000000) return 7;
    if (volume <= 3000000) return 8;
    if (volume <= 4000000) return 9;
    if (volume <= 6000000) return 10;
    if (volume <= 8000000) return 11;
    return 12; // for volume > 8M
  };

  // Calculate petal size based on volatility
  const getPetalSize = () => {
    const volatility = parseFloat(stock.volatility);
    return 30 + (volatility * 200);
  };

  // Calculate center size based on market cap (in billions)
  const getCenterSize = () => {
    const marketCap = parseFloat(stock.marketCapB);
    // Use log scale for market cap since it varies widely
    // Scale from 3-12px based on market cap
    if (marketCap < 10) return 1;       // < $10B (small cap)
    if (marketCap < 50) return 2;       // $10B - $50B (mid cap)
    if (marketCap < 100) return 3;      // $50B - $100B (large cap)
    if (marketCap < 250) return 4;      // $100B - $250B (mega cap)
    if (marketCap < 500) return 5;     // $250B - $500B (mega cap)
    return 6;                          // > $500B (ultra mega cap)
  };

  const flowerColor = getFlowerColor();
  const numberOfPetals = getNumberOfPetals();
  const petalSize = getPetalSize();
  const centerSize = getCenterSize();

  return (
    <FlowerContainer>
      <DetailCard isSearchResult={isSearchResult}>
        <TitleRow>
          <DetailTitle>{stock.name}</DetailTitle>
          <RecommendationTag type={getRecommendation()}>
            {getRecommendation().toUpperCase()}
          </RecommendationTag>
        </TitleRow>
        <DetailText>
          {getRecommendation() === 'buy' 
            ? `Looking good! This is a ${
                parseFloat(stock.marketCapB) >= 200 
                  ? "very large, well-established company" 
                  : parseFloat(stock.marketCapB) >= 10
                    ? "medium-sized growing company"
                    : "smaller company with growth potential"
              }. ${
                parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
                  ? "Lots of people are trading this stock today, which supports the upward movement."
                  : "Not many trades happening yet - wait for more people to join in."
              } ${
                parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
                  ? "The price is moving smoothly without big swings - a healthy sign."
                  : "The price is jumping around a bit - consider buying in smaller amounts."
              }`
            : getRecommendation() === 'sell'
            ? `Proceed with caution on this ${
                parseFloat(stock.marketCapB) >= 200
                  ? "major company showing signs of weakness"
                  : parseFloat(stock.marketCapB) >= 10
                    ? "mid-sized company facing some challenges"
                    : "smaller company with increasing risks"
              }. ${
                parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
                  ? "Many investors are selling, which could push the price lower."
                  : "Trading is light - you might get better selling prices by waiting."
              } ${
                parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
                  ? "Even though the price isn't swinging wildly, the downward trend is concerning."
                  : "The price is making big swings, suggesting uncertainty in the market."
              }`
            : `Stay steady with this ${
                parseFloat(stock.marketCapB) >= 200
                  ? "major, stable company"
                  : parseFloat(stock.marketCapB) >= 10
                    ? "established mid-sized company"
                    : "promising smaller company"
              }. ${
                parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
                  ? "Normal amount of trading today - nothing unusual to worry about."
                  : "Trading is quiet, which is typical when the market is making up its mind."
              } ${
                parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
                  ? "Price is moving steadily without drama - typical for healthy stocks."
                  : "Keep an eye on the price swings - they might signal upcoming changes."
              }`
          }
        </DetailText>
        <DetailText>
          <MetricLabel>Volume:</MetricLabel> 
          <MetricValue>{parseInt(stock.volume).toLocaleString()} SHARES TRADED</MetricValue>
          <Annotation>
            {parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 5000000 : parseFloat(stock.marketCapB) >= 10 ? 2000000 : 1000000)
              ? "High trading activity today"
              : parseInt(stock.volume) > (parseFloat(stock.marketCapB) >= 200 ? 2500000 : parseFloat(stock.marketCapB) >= 10 ? 1000000 : 500000)
              ? "Average trading activity"
              : "Lower than usual trading"
            } (shown by number of flower petals: more petals = more trading)
          </Annotation>
        </DetailText>
        <DetailText>
          <MetricLabel>Market Cap:</MetricLabel> 
          <MetricValue>${parseFloat(stock.marketCapB).toFixed(2)}B</MetricValue>
          <Annotation>
            {parseFloat(stock.marketCapB) >= 200
              ? "Very large company (over $200B)"
              : parseFloat(stock.marketCapB) >= 10
              ? "Mid-sized company ($10B-$200B)"
              : "Smaller company (under $10B)"
            } (shown by flower center size: bigger center = bigger company)
          </Annotation>
        </DetailText>
        <DetailText>
          <MetricLabel>Volatility:</MetricLabel> 
          <MetricValue>{parseFloat(stock.volatility).toFixed(2)}</MetricValue>
          <Annotation>
            {parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.05 : parseFloat(stock.marketCapB) >= 10 ? 0.08 : 0.10)
              ? "Price moves smoothly without big swings"
              : parseFloat(stock.volatility) < (parseFloat(stock.marketCapB) >= 200 ? 0.08 : parseFloat(stock.marketCapB) >= 10 ? 0.12 : 0.15)
              ? "Normal price movements for this type of stock"
              : "Larger than usual price swings"
            } (shown by petal size: bigger petals = bigger price swings)
          </Annotation>
        </DetailText>
      </DetailCard>

      <FlowerSvg>
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          {[...Array(numberOfPetals)].map((_, i) => {
            const angle = (i * (360 / numberOfPetals)) * (Math.PI / 180);
            const midPoint = petalSize * 0.7;
            
            const petalPath = `
              M 50 50
              C ${50 + Math.cos(angle - 0.6) * midPoint * 0.5} ${50 + Math.sin(angle - 0.6) * midPoint * 0.5},
                ${50 + Math.cos(angle - 0.3) * midPoint * 0.8} ${50 + Math.sin(angle - 0.3) * midPoint * 0.8},
                ${50 + Math.cos(angle) * midPoint} ${50 + Math.sin(angle) * midPoint}
              C ${50 + Math.cos(angle + 0.3) * midPoint * 0.8} ${50 + Math.sin(angle + 0.3) * midPoint * 0.8},
                ${50 + Math.cos(angle + 0.6) * midPoint * 0.5} ${50 + Math.sin(angle + 0.6) * midPoint * 0.5},
                50 50
              Z
            `;
            
            return (
              <path
                key={i}
                d={petalPath}
                fill={flowerColor}
                stroke="#2C3E50"
                strokeWidth="0.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            );
          })}
          
          <circle cx="50" cy="50" r={centerSize} fill="#2C3E50" />
        </svg>
      </FlowerSvg>

      <StockInfo>
        <div>{stock.symbol}</div>
        <div>
          ${parseFloat(stock.price).toFixed(2)} | {parseFloat(stock.changePercent) > 0 ? '+' : ''}{parseFloat(stock.changePercent).toFixed(2)}%
        </div>
      </StockInfo>
    </FlowerContainer>
  );
};

export default Flower;